import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Calendar, Clock, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "meeting",  label: "Treffen / Beratung" },
  { v: "call",     label: "Telefonat" },
  { v: "renewal",  label: "Erneuerung" },
  { v: "reminder", label: "Erinnerung" },
  { v: "task",     label: "Aufgabe" },
  { v: "other",    label: "Sonstiges" },
];

const COLORS = [
  { v: "amber",   ring: "ring-amber-500",   bg: "bg-amber-500" },
  { v: "emerald", ring: "ring-emerald-500", bg: "bg-emerald-500" },
  { v: "blue",    ring: "ring-blue-500",    bg: "bg-blue-500" },
  { v: "violet",  ring: "ring-violet-500",  bg: "bg-violet-500" },
  { v: "red",     ring: "ring-red-500",     bg: "bg-red-500" },
  { v: "stone",   ring: "ring-stone-500",   bg: "bg-stone-500" },
];

const EMPTY = {
  title: "", date: "", time: "", duration_min: 60,
  kind: "meeting", color: "amber", location: "", notes: "",
  client_id: null,
};

export default function AppointmentDialog({ open, onClose, defaultDate, existing, onSaved, presetDraft }) {
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const isEdit = !!existing?.appointment_id || !!existing?.id;

  // Initialize form when opened
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        title: existing.title || "",
        date: existing.date || defaultDate || "",
        time: existing.time || "",
        duration_min: existing.duration_min || 60,
        kind: existing.kind || "meeting",
        color: existing.color || "amber",
        location: existing.location || "",
        notes: existing.notes || "",
        client_id: existing.client_id || null,
      });
    } else if (presetDraft) {
      // Pre-filled from external source (e.g. AI extracted from email)
      setForm({
        ...EMPTY,
        title: presetDraft.title || "",
        date: presetDraft.date || defaultDate || "",
        time: presetDraft.time || "",
        duration_min: presetDraft.duration_min || 60,
        kind: presetDraft.kind || "meeting",
        color: presetDraft.color || "amber",
        location: presetDraft.location || "",
        notes: presetDraft.notes || "",
        client_id: presetDraft.client_id || null,
      });
    } else {
      setForm({ ...EMPTY, date: defaultDate || "" });
    }
    // Load clients (lightweight)
    api.get("/clients", { params: { limit: 200 } })
      .then(r => setClients(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setClients([]));
  }, [open, existing, defaultDate, presetDraft]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    if (!form.date) { toast.error("Datum erforderlich"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        time: form.time || null,
        client_id: form.client_id || null,
      };
      if (isEdit) {
        const id = existing.appointment_id || existing.id;
        await api.patch(`/calendar/appointments/${id}`, payload);
        toast.success("Termin aktualisiert");
      } else {
        await api.post("/calendar/appointments", payload);
        toast.success("Termin erstellt");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Termin löschen?")) return;
    setBusy(true);
    try {
      const id = existing.appointment_id || existing.id;
      await api.delete(`/calendar/appointments/${id}`);
      toast.success("Termin gelöscht");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const sendInvitation = async () => {
    if (!isEdit) {
      toast.error("Bitte zuerst Termin speichern");
      return;
    }
    if (!form.client_id) {
      toast.error("Kein Kunde verknüpft");
      return;
    }
    setSendingInvite(true);
    try {
      const id = existing.appointment_id || existing.id;
      const r = await api.post(`/calendar/appointments/${id}/send-invitation`);
      if (r.data?.status === "sent") {
        toast.success(`Termin-Einladung gesendet an ${r.data.to}`);
      } else if (r.data?.status === "pending") {
        toast.info("Einladung in Warteschlange (Resend Key fehlt)");
      } else {
        toast.error(`Einladung fehlgeschlagen: ${r.data?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Termin bearbeiten" : "Neuer Termin"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <Label className="label-uppercase text-[10px]">Titel</Label>
            <Input
              autoFocus
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="z.B. Beratungstermin mit Herrn Müller"
              data-testid="appt-title-input"
            />
          </div>

          {/* Date + Time + Duration row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-uppercase text-[10px] flex items-center gap-1"><Calendar className="w-3 h-3" /> Datum</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                data-testid="appt-date-input"
              />
            </div>
            <div>
              <Label className="label-uppercase text-[10px] flex items-center gap-1"><Clock className="w-3 h-3" /> Uhrzeit</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => set("time", e.target.value)}
                data-testid="appt-time-input"
              />
            </div>
            <div>
              <Label className="label-uppercase text-[10px]">Dauer (Min)</Label>
              <Input
                type="number" min="0" step="15"
                value={form.duration_min}
                onChange={(e) => set("duration_min", parseInt(e.target.value || "0", 10))}
                data-testid="appt-duration-input"
              />
            </div>
          </div>

          {/* Kind + Color */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase text-[10px]">Art</Label>
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="appt-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase text-[10px]">Farbe</Label>
              <div className="flex gap-2 mt-2" data-testid="appt-color-palette">
                {COLORS.map(c => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => set("color", c.v)}
                    className={`w-7 h-7 rounded-full ${c.bg} transition-all ${form.color === c.v ? `ring-2 ring-offset-2 ${c.ring}` : "opacity-60 hover:opacity-100"}`}
                    aria-label={c.v}
                    data-testid={`appt-color-${c.v}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Client link */}
          <div>
            <Label className="label-uppercase text-[10px]">Kunde (optional)</Label>
            <Select value={form.client_id || "__none__"} onValueChange={(v) => set("client_id", v === "__none__" ? null : v)}>
              <SelectTrigger data-testid="appt-client-select"><SelectValue placeholder="Kein Kunde" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">— Kein Kunde —</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.client_type === "business" ? (c.company_name || `${c.first_name} ${c.last_name}`) : `${c.first_name} ${c.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div>
            <Label className="label-uppercase text-[10px] flex items-center gap-1"><MapPin className="w-3 h-3" /> Ort</Label>
            <Input
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="z.B. Büro Zürich oder Online"
              data-testid="appt-location-input"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="label-uppercase text-[10px]">Notizen</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Optionale Notizen…"
              rows={3}
              data-testid="appt-notes-input"
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between gap-2">
          {isEdit ? (
            <Button variant="outline" onClick={remove} disabled={busy} className="text-red-600 hover:bg-red-50" data-testid="appt-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Löschen
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            {isEdit && form.client_id && (
              <Button
                variant="outline"
                onClick={sendInvitation}
                disabled={busy || sendingInvite}
                title="Einladung mit .ics-Datei per E-Mail an den Kunden senden"
                data-testid="appt-send-invite-btn"
              >
                {sendingInvite ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Einladung senden
              </Button>
            )}
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="appt-cancel-btn">Abbrechen</Button>
            <Button onClick={save} disabled={busy} className="btn-prime" data-testid="appt-save-btn">
              {isEdit ? "Speichern" : "Erstellen"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
